/**
 * useEditingSourceSync — the editing page's view of its source visuals.
 *
 * The claim onto `attachments` and the per-visual quantity are the same problem
 * every upload-taking product has, so they live in `useSourceVisualsClaim`. What
 * is editing-specific stays here: mirroring the claimed filenames into the flow's
 * own DTO, and whether this product's sky settings are collected on this page.
 *
 * THE MIRROR reads `attachments`, not the upload scope: attachments is the
 * durable record, correct on resume and correct immediately after a delete, so
 * the DTO may follow it in BOTH directions. It is value-guarded on the filename
 * list because `markConfigDirty` is value-blind — an unguarded write on every
 * provider re-render would un-confirm a saved configuration at random.
 */

import { useEffect } from 'react'

import type { CartItemId } from 'models/purchaseFlowRevamp'

import { usePfStore } from '../../store'
import { makeConfigKey } from '../../store/slices/productConfigSlice'
import { useSourceVisualsClaim, useVariantInputImagePolicy } from '../sourceVisuals'
import { useVariantCollectsAiSettings } from './useVariantCollectsAiSettings'
import type { EditingConfigDraftDTO } from './editingConfig.dto'

export interface EditingSourceScope {
  /** Product-GROUP code the uploads are grouped under in the shared FileAPI scope. */
  scopeKey: string | null
  /** Selected variant's catalogue code — sent to the BE on the upload-URL request. */
  variantId: string | null
  /** Filenames other cart lines already own, so this line cannot re-offer them. */
  claimedByOtherLines: ReadonlySet<string>
  /** Files this line has actually claimed — what the order will carry. */
  claimedFileCount: number
  /** Whether one produced item is bought per uploaded file. */
  isOnePerItem: boolean
  /** Whether this product's sky settings are collected on this page. */
  collectsAiSettings: boolean
}

export function useEditingSourceSync(cartItemId: CartItemId): EditingSourceScope {
  const policy = useVariantInputImagePolicy(cartItemId)
  const collectsAiSettings = useVariantCollectsAiSettings(cartItemId)
  const { scopeKey, variantId, claimedByOtherLines, claimedFilenames, isOnePerItem } =
    useSourceVisualsClaim(cartItemId, policy)

  const claimedKey = claimedFilenames.join(' ')

  useEffect(() => {
    const store = usePfStore.getState()

    const stored = store.productConfigs[makeConfigKey(cartItemId, 'editing_config')] as
      EditingConfigDraftDTO | undefined
    if ((stored?.inputImages ?? []).join(' ') === claimedKey) return

    store.setConfig(cartItemId, 'editing_config', { inputImages: claimedKey === '' ? [] : claimedKey.split(' ') })
    // A file added or removed after Save is a post-Save edit: the client has to
    // confirm the set they are actually buying.
    store.markConfigDirty(cartItemId, 'editing_config')
  }, [claimedKey, cartItemId])

  return {
    scopeKey,
    variantId,
    claimedByOtherLines,
    claimedFileCount: claimedFilenames.length,
    isOnePerItem,
    collectsAiSettings,
  }
}
